import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, UsersRound } from "lucide-react";
import Portrait from "@/components/Portrait";
import { papi, errText } from "../portalApi";
import { Card, EmptyState, PageHeader, Spinner, StatusBadge, fmtTime } from "../ui";

export default function MyTeam() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await papi.get("/myteam");
      setData(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not load your team."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Spinner />;

  const pendingTotal = data.pending.leaves + data.pending.regularisations + data.pending.timesheets;

  return (
    <div data-testid="myteam-page">
      <PageHeader title="My Team" sub="Your direct reports and their status today." />

      {pendingTotal > 0 && (
        <Card className="mb-6 border-gold/40 bg-gold/5" testid="myteam-pending-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-navy-900">
              {pendingTotal} request(s) awaiting your approval
              <span className="ml-2 text-xs font-normal text-slate-500">
                {data.pending.leaves} leave · {data.pending.regularisations} regularisation · {data.pending.timesheets} timesheet
              </span>
            </p>
            <Link to="/portal/approvals" className="link-underline text-xs" data-testid="myteam-goto-approvals">
              Review now <ArrowRight className="h-3 w-3" aria-hidden />
            </Link>
          </div>
        </Card>
      )}

      {data.reports.length === 0 ? (
        <EmptyState icon={UsersRound} title="No direct reports" sub="Team members assigned to you will appear here." testid="myteam-empty" />
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="myteam-grid">
          {data.reports.map((r) => (
            <Card key={r.id} className="text-center" testid={`myteam-card-${r.id}`}>
              <Portrait name={r.fullName} src={r.photograph || ""} size="md" className="mx-auto" testid={`myteam-photo-${r.id}`} />
              <h3 className="mt-4 font-serif text-lg text-navy-900">{r.fullName}</h3>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gold-dark">{r.designation || "Team Member"}</p>
              <p className="mt-1 text-xs text-slate-400">{r.employmentType}</p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <StatusBadge status={r.todayStatus} />
              </div>
              {r.checkIn && (
                <p className="mt-2 text-[11px] text-slate-400">Checked in {fmtTime(r.checkIn)}</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
